'use client';

import { useEffect, useRef, useState } from 'react';
import { CompanyProfile, Job } from '@/interfaces/workable';
import { WORKABLE_API_URL } from '@/utils/workable';

export interface UseWorkableJobsOptions {
  /**
   * Jobs already fetched on the server (via `fetchWorkableJobs`) and passed
   * through props. When provided and non-empty, the hook skips the client
   * fetch entirely; when empty (server fetch failed or returned nothing),
   * the hook falls back to fetching on the client.
   */
  initialJobs?: Job[];
}

export interface UseWorkableJobsResult {
  jobs: Job[] | null;
  isLoading: boolean;
  error: Error | null;
}

export function useWorkableJobs({
  initialJobs,
}: UseWorkableJobsOptions = {}): UseWorkableJobsResult {
  const hasInitial = !!initialJobs && initialJobs.length > 0;
  const hasFetchedRef = useRef(hasInitial);
  const [jobs, setJobs] = useState<Job[] | null>(
    hasInitial ? (initialJobs as Job[]) : null,
  );
  const [isLoading, setIsLoading] = useState(!hasInitial);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (hasFetchedRef.current) {
      return;
    }
    hasFetchedRef.current = true;

    const load = async () => {
      try {
        const res = await fetch(WORKABLE_API_URL);
        if (!res.ok) {
          throw new Error(res.statusText);
        }
        const data: CompanyProfile = await res.json();
        setJobs(data.jobs ?? []);
      } catch (err) {
        console.error('[useWorkableJobs] Workable fetch error:', err);
        setJobs(null);
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, []);

  return { jobs, isLoading, error };
}
